"use client";

import React, { useState, useEffect } from "react";
import { motion } from "motion/react";
import { Plus, Trash2, Edit2, Save, X, RefreshCw, FileText } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  content: any;
  image_url: string;
  category: string;
  tags: string[];
  published_at: string;
}

export function BlogManager() {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchPosts = async () => {
    setIsLoading(true);
    const { data, error } = await supabase
      .from('blog_posts')
      .select('*')
      .order('published_at', { ascending: false });
    
    if (error) toast.error("Failed to fetch posts");
    else setPosts(data || []);
    setIsLoading(false);
  };

  useEffect(() => {
    fetchPosts();
  }, []);

  const handleDelete = async (id: string) => {
    if (!confirm("Delete this post?")) return;
    const { error } = await supabase.from('blog_posts').delete().eq('id', id);
    if (error) toast.error(error.message);
    else fetchPosts();
  };

  if (isLoading) return <div className="p-20 text-center"><RefreshCw className="animate-spin mx-auto text-cyan-500" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-xl font-black uppercase tracking-tight text-white">Manage Blog</h3>
        <button className="flex items-center gap-2 px-4 py-2 bg-cyan-600 text-white rounded-xl text-xs font-black uppercase tracking-widest hover:bg-cyan-500 transition-all">
          <Plus size={16} /> New Post
        </button>
      </div>

      <div className="grid gap-4">
        {posts.map(post => (
          <div key={post.id} className="p-6 bg-neutral-900/40 border border-neutral-800 rounded-3xl flex items-center justify-between group">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-cyan-600/20 flex items-center justify-center text-cyan-400">
                <FileText size={20} />
              </div>
              <div>
                <h4 className="font-black text-white uppercase tracking-tight">{post.title}</h4>
                <p className="text-[10px] text-neutral-500 font-bold uppercase tracking-widest">{post.category} • {new Date(post.published_at).toLocaleDateString()}</p>
              </div>
            </div>
            <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
              <button onClick={() => handleDelete(post.id)} className="p-2 text-red-500 hover:bg-red-500/10 rounded-lg"><Trash2 size={18} /></button>
            </div>
          </div>
        ))}
        {posts.length === 0 && (
          <div className="p-20 text-center bg-neutral-900/20 rounded-3xl border border-dashed border-neutral-800">
            <p className="text-neutral-500 font-bold uppercase tracking-widest text-xs">No blog posts yet</p>
          </div>
        )}
      </div>
    </div>
  );
}